import React from 'react';
import { Typography, Grid, TextField, Stack, Box, Button, Avatar } from '@mui/material';
import { CloudUpload as UploadIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Controller } from 'react-hook-form';

/**
 * Reusable billing profile (issuer) form fields.
 *
 * Props:
 *  - control        – react-hook-form control object
 *  - errors         – react-hook-form errors object
 *  - logoPreview    – (optional) URL string for logo preview
 *  - onLogoUpload   – (optional) file input onChange handler
 *  - onCopyProfile  – (optional) handler for "Copy from my profile" button
 *  - compact        – (optional) if true, hides logo upload, copy button, bank details and payment terms
 */
const IssuerProfileForm = ({ control, errors, logoPreview, onLogoUpload, onCopyProfile, compact = false }) => {
  return (
    <Stack spacing={3}>
      {/* Logo Upload — only shown in full mode */}
      {!compact && onLogoUpload && (
        <Box display="flex" alignItems="center" gap={3}>
          <Avatar
            src={logoPreview}
            sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: 32 }}
          >
            {!logoPreview && 'L'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" mb={0.5}>Company Logo</Typography>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
            >
              Upload Logo
              <input type="file" hidden accept="image/*" onChange={onLogoUpload} />
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
              PNG or JPG, max 2MB. Appears on your invoices.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Copy from profile — only shown in full mode */}
      {!compact && onCopyProfile && (
        <Button
          variant="text"
          startIcon={<CopyIcon />}
          onClick={onCopyProfile}
          sx={{ alignSelf: 'flex-start' }}
        >
          Copy from my profile
        </Button>
      )}

      {/* Business Details */}
      {!compact && <Typography variant="h6" fontWeight={600}>Business Details</Typography>}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Your Name *" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Business / Trading Name" fullWidth />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="abn"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="ABN" fullWidth error={!!errors.abn} helperText={errors.abn?.message} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Email *" fullWidth error={!!errors.email} helperText={errors.email?.message} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Phone" fullWidth />
            )}
          />
        </Grid>
      </Grid>

      {/* Address */}
      {!compact && (
        <>
          <Typography variant="h6" fontWeight={600}>Address</Typography>
          <Controller
            name="address.street"
            control={control}
            render={({ field }) => <TextField {...field} label="Street Address" fullWidth />}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => <TextField {...field} label="City / Suburb" fullWidth />}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Controller
                name="address.state"
                control={control}
                render={({ field }) => <TextField {...field} label="State" fullWidth />}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Controller
                name="address.postcode"
                control={control}
                render={({ field }) => <TextField {...field} label="Postcode" fullWidth />}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Bank Details — only shown in full mode */}
      {!compact && (
        <>
          <Typography variant="h6" fontWeight={600}>Bank Details</Typography>
          <Typography variant="body2" color="text.secondary">
            These will appear on your invoices so clients know where to pay.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="bankDetails.bankName"
                control={control}
                render={({ field }) => <TextField {...field} label="Bank Name" fullWidth />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="bankDetails.accountName"
                control={control}
                render={({ field }) => <TextField {...field} label="Account Name" fullWidth />}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="bankDetails.bsb"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="BSB" fullWidth placeholder="123-456" error={!!errors.bankDetails?.bsb} helperText={errors.bankDetails?.bsb?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="bankDetails.accountNumber"
                control={control}
                render={({ field }) => <TextField {...field} label="Account Number" fullWidth />}
              />
            </Grid>
          </Grid>

          {/* Payment Terms */}
          <Controller
            name="paymentTerms"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Default Payment Terms"
                fullWidth
                placeholder="e.g., Net 30, Due on receipt, Payment within 14 days"
              />
            )}
          />
        </>
      )}

      {compact && (
        <Typography variant="caption" color="text.secondary">
          You can add address, bank details and more in your Billing Profile page.
        </Typography>
      )}
    </Stack>
  );
};

export default IssuerProfileForm;
